import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, ClipboardList, Download, FileAudio, Eye, History, Loader2, Pencil, Plus, RefreshCw, Save, Trash2,
  Volume2, X,
} from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import CdrTablePagination from "@/components/cdr/CdrTablePagination";
import CdrRecordingPicker from "@/components/cdr/CdrRecordingPicker";
import useCdrStagingBasket from "@/hooks/useCdrStagingBasket";
import SearchableAgentSelect from "@/components/common/SearchableAgentSelect";
import {
  PanelTableActions, PanelTableBody, PanelTableCell, PanelTableEmpty, PanelTableHead,
  PanelTableHeadRow, PanelTableRow, TableHeader,
} from "@/components/ui/panel-table";
import {
  agentsForSearchSelect, buildExcelRows, createDraftRow, EMPTY_TL_RECORDING_FORM, formatOnayLabel, formatReportDate,
  buildReportListWeekDateParams, formRowToPayload, hasReportText, mapItemToForm, recordingFilenameFromUrl,
  REPORT_LIST_FILTER_ALL, REPORT_LIST_FILTER_DATE, REPORT_LIST_FILTER_WEEK, resolveLastWeekNumber,
  sanitizeExcelSheetName, TL_TABLE_COLUMNS, truncateReportText,
} from "@/utils/tlRecordingReportHelpers";
import TlIsoWeekPicker from "@/components/reports/TlIsoWeekPicker";
import {
  formatWeekRangeLabel, getCurrentIsoWeekContext, getIsoWeekRange, resolveIsoYearForRow,
} from "@/utils/isoWeek";
import { storeKalitePrefillFromTl } from "@/utils/managerReportHelpers";

const ONAY_SELECT_EMPTY = "__empty__";
import {
  TlRecordingAudioModal,
  TlRecordingUrlActions,
  buildReportRecordingInfo,
} from "@/components/reports/tlRecordingAudio";

const TL_COLUMN_META = {
  assistant_name: { minW: "min-w-[100px]", head: "sticky left-0 z-20" },
  week_number: { minW: "min-w-[148px]", align: "left" },
  report_date: { minW: "min-w-[88px]", align: "center" },
  assistant_report: { minW: "min-w-[160px]", text: true },
  ask_onay: { minW: "min-w-[108px]", align: "center" },
  ordeleme: { minW: "min-w-[96px]", align: "center" },
  mr_isteme: { minW: "min-w-[96px]", align: "center" },
  mr_tepinme: { minW: "min-w-[96px]", align: "center" },
  tedirginlik: { minW: "min-w-[96px]", align: "center" },
  uzmanlik: { minW: "min-w-[96px]", align: "center" },
  leader_report: { minW: "min-w-[160px]", text: true },
  tksk_onay: { minW: "min-w-[108px]", align: "center" },
  call1_recording_url: { minW: "w-[58px] max-w-[58px]", align: "center", narrow: true },
  call2_recording_url: { minW: "w-[58px] max-w-[58px]", align: "center", narrow: true },
  sample1_recording_url: { minW: "w-[58px] max-w-[58px]", align: "center", narrow: true },
  sample2_recording_url: { minW: "w-[58px] max-w-[58px]", align: "center", narrow: true },
};

const TL_COLUMN_HEAD_LINES = {
  assistant_name: ["Asistan İsmi"],
  week_number: ["Hafta"],
  report_date: ["Tarih"],
  assistant_report: ["Asistan Ses Kaydı Raporu"],
  ask_onay: ["ASK", "Onayı"],
  ordeleme: ["İrdeleme"],
  mr_isteme: ["MR", "İsteme"],
  mr_tepinme: ["MR Üstünde", "Tepinme"],
  tedirginlik: ["Tedirginlik"],
  uzmanlik: ["Uzmanlık"],
  leader_report: ["Tk Lideri Ses Kaydı Raporu"],
  tksk_onay: ["TKSK", "Onayı"],
  call1_recording_url: ["1. Görüşme", "Ses kaydı"],
  call2_recording_url: ["2. Görüşme", "Ses kaydı"],
  sample1_recording_url: ["Örnek 1.", "Ses kaydı"],
  sample2_recording_url: ["Örnek 2.", "Ses kaydı"],
};

const colMeta = (key) => TL_COLUMN_META[key] || {};

const getTlCellWidthClass = (meta) => {
  if (meta.compact) return meta.minW || "";
  if (meta.text) return `${meta.minW || ""} max-w-[260px]`;
  if (meta.narrow) return meta.minW || "";
  return meta.minW || "";
};

const TlRecordingTableHeadLabel = ({ lines, align = "left" }) => {
  const safeLines = (lines || []).filter(Boolean);
  const alignClass = align === "center" ? "items-center text-center" : "items-start text-left";

  if (safeLines.length <= 1) {
    return (
      <span className={`block text-[11px] leading-snug whitespace-nowrap ${align === "center" ? "text-center" : "text-left"}`}>
        {safeLines[0] || ""}
      </span>
    );
  }

  return (
    <span className={`inline-flex flex-col text-[10px] leading-tight gap-0.5 whitespace-normal ${alignClass}`}>
      {safeLines.map((line, index) => (
        <span key={`${line}-${index}`} className="block">{line}</span>
      ))}
    </span>
  );
};

const PAGE_SIZE = 25;
const ACTIVITY_LOG_PAGE_SIZE = 30;

const TL_ACTIVITY_ACTION_LABELS = {
  create: "Kayıt eklendi",
  update: "Güncelleme",
  delete: "Silindi",
  approve: "Onay alanı",
  import: "Excel içe aktarma",
};

const formatActivityTimestamp = (value) => {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
};

const formatAuditMetaLine = (row) => {
  const parts = [];
  if (row.created_by_name || row.created_at) {
    parts.push(`Oluşturan: ${row.created_by_name || "—"} (${formatActivityTimestamp(row.created_at)})`);
  }
  if (row.updated_at && row.updated_at !== row.created_at) {
    parts.push(`Son güncelleme: ${row.updated_by_name || "—"} (${formatActivityTimestamp(row.updated_at)})`);
  }
  return parts.join(" · ");
};
const RECORDING_URL_FIELDS = [
  { key: "call1_recording_url", label: "1. Görüşme Ses kaydı" },
  { key: "call2_recording_url", label: "2. Görüşme Ses kaydı" },
  { key: "sample1_recording_url", label: "Örnek 1. Ses kaydı" },
  { key: "sample2_recording_url", label: "Örnek 2. Ses kaydı" },
];

const TlOnaySelectCell = ({
  row, field, canEdit, isDark, inputClass, onSave, savingId,
}) => {
  const raw = row?.[field] || "";
  const selectValue = raw === "evet" || raw === "hayir" ? raw : ONAY_SELECT_EMPTY;
  const saving = savingId === row?.id;

  if (!row?.id) {
    return <span className={`text-[11px] ${isDark ? "text-slate-500" : "text-gray-400"}`}>—</span>;
  }

  if (!canEdit) {
    const label = formatOnayLabel(raw);
    return (
      <span className={`text-[11px] font-medium ${label ? (isDark ? "text-slate-200" : "text-gray-800") : (isDark ? "text-slate-500" : "text-gray-400")}`}>
        {label || "Seçiniz"}
      </span>
    );
  }

  return (
    <Select
      value={selectValue}
      disabled={saving}
      onValueChange={(v) => {
        const next = v === ONAY_SELECT_EMPTY ? "" : v;
        onSave(row.id, field, next);
      }}
    >
      <SelectTrigger className={`h-8 text-[11px] w-[100px] ${inputClass}`}>
        <SelectValue placeholder="Seçiniz" />
      </SelectTrigger>
      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
        <SelectItem value={ONAY_SELECT_EMPTY}>Seçiniz</SelectItem>
        <SelectItem value="evet">Evet</SelectItem>
        <SelectItem value="hayir">Hayır</SelectItem>
      </SelectContent>
    </Select>
  );
};

const renderTableCell = (row, col, {
  textClass, labelClass, onOpenDetail, apiBase, isDark, onPlayRecording, contextIsoYear,
}) => {
  const value = row?.[col.key];
  if (col.key === "assistant_name") {
    const name = value || "—";
    const initial = name !== "—" ? name.charAt(0).toLocaleUpperCase("tr-TR") : "?";
    return (
      <div className="flex items-center gap-2.5 min-w-[140px]">
        <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-2 ring-offset-1 ${
          isDark
            ? "bg-violet-500/20 text-violet-200 ring-violet-500/30 ring-offset-slate-900"
            : "bg-violet-100 text-violet-700 ring-violet-200 ring-offset-white"
        }`}>
          {initial}
        </span>
        <div className="min-w-0">
          <div className="font-semibold truncate text-[13px]">{name}</div>
          {row.agent_name && <div className={`text-[10px] truncate ${labelClass}`}>{row.agent_name}</div>}
        </div>
      </div>
    );
  }
  if (col.key === "week_number") {
    if (!value && value !== 0) return <span className={labelClass}>—</span>;
    const isoYear = resolveIsoYearForRow(row, contextIsoYear);
    const rangeLabel = formatWeekRangeLabel(isoYear, Number(value));
    return (
      <span className={`text-[11px] whitespace-nowrap ${isDark ? "text-slate-200" : "text-gray-800"}`}>
        {rangeLabel}
      </span>
    );
  }
  if (col.type === "date") {
    return (
      <span className={`whitespace-nowrap text-[12px] tabular-nums ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        {formatReportDate(value)}
      </span>
    );
  }
  if (col.type === "approval") {
    const label = formatOnayLabel(value);
    return (
      <span className={`text-[11px] font-medium ${label ? (isDark ? "text-slate-200" : "text-gray-800") : (isDark ? "text-slate-500" : "text-gray-400")}`}>
        {label || "Seçiniz"}
      </span>
    );
  }
  if (col.type === "text") {
    const text = String(value || "").trim();
    if (!text) return <span className={`text-[11px] ${labelClass}`}>—</span>;
    return (
      <button
        type="button"
        onClick={() => onOpenDetail(row, col.key)}
        className={`text-left w-full rounded-md line-clamp-3 leading-relaxed transition-colors px-2 py-1.5 -mx-2 text-[12px] ${
          isDark
            ? "hover:bg-slate-800/60 text-slate-200"
            : "hover:bg-violet-50 text-gray-700"
        }`}
        title={text}
      >
        {truncateReportText(text, 100)}
      </button>
    );
  }
  if (col.type === "url") {
    return (
      <TlRecordingUrlActions
        url={value}
        apiBase={apiBase}
        isDark={isDark}
        labelClass={labelClass}
        onPlay={(recordingUrl) => onPlayRecording(recordingUrl, `${col.label} · ${row.assistant_name || ""}`)}
      />
    );
  }
  return value ?? "—";
};

const TlRecordingInlineCell = ({
  col, row, isEditing, onFieldChange, onFieldsChange, searchableAgents, isDark, inputClass, labelClass,
  textClass, apiBase, onOpenPicker, onOpenDetail, onPlayRecording, pickerContext, cdrBasketCount = 0,
  contextIsoYear,
}) => {
  const value = row[col.key] ?? "";
  const cellInput = `${inputClass} h-8 text-xs`;
  const cellTextarea = `${inputClass} text-xs min-h-[72px] resize-y`;

  if (col.type === "approval") {
    return null;
  }

  if (!isEditing) {
    return renderTableCell(row, col, {
      textClass, labelClass, onOpenDetail, apiBase, isDark, onPlayRecording, contextIsoYear,
    });
  }

  if (col.key === "week_number") {
    const isoYear = resolveIsoYearForRow(row, contextIsoYear);
    const rawWeek = value === "" || value == null ? resolveLastWeekNumber([], []) : Number(value);
    const weekVal = Number.isFinite(rawWeek) && rawWeek >= 1 ? rawWeek : resolveLastWeekNumber([], []);
    return (
      <TlIsoWeekPicker
        compact
        isoYear={isoYear}
        week={weekVal}
        isDark={isDark}
        inputClass={inputClass}
        onChange={({ week, start }) => {
          onFieldsChange({ week_number: week, report_date: start });
        }}
      />
    );
  }

  if (col.key === "assistant_name") {
    const selectedAgent = searchableAgents.find((a) => String(a.id) === String(row.agent_id))
      || (value ? { ad_soyad: value } : null);
    return (
      <SearchableAgentSelect
        agents={searchableAgents}
        value={selectedAgent}
        onChange={(agent) => onFieldsChange({
          agent_id: agent ? String(agent.id) : "",
          assistant_name: agent?.ad_soyad || "",
        })}
        placeholder="Asistan seç"
        isDark={isDark}
        inputClass={inputClass}
        labelClass={labelClass}
        className="w-full min-w-[140px]"
      />
    );
  }

  if (col.type === "date") {
    return (
      <Input
        type="date"
        value={value}
        onChange={(e) => onFieldChange(col.key, e.target.value)}
        className={cellInput}
      />
    );
  }

  if (col.type === "text") {
    return (
      <Textarea
        rows={3}
        value={value}
        onChange={(e) => onFieldChange(col.key, e.target.value)}
        className={cellTextarea}
        placeholder={col.label}
      />
    );
  }

  if (col.type === "url") {
    const canPickFromBasket = cdrBasketCount > 0;
    return (
      <div className="flex gap-1">
        <Input
          value={value}
          onChange={(e) => onFieldChange(col.key, e.target.value)}
          className={`${cellInput} font-mono flex-1 min-w-0`}
          placeholder="https://..."
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={!canPickFromBasket}
          className={`h-8 w-8 shrink-0 ${canPickFromBasket ? "" : "opacity-45 cursor-not-allowed"}`}
          title={
            canPickFromBasket
              ? `CDR sepetinden seç (${cdrBasketCount} kayıt)`
              : "CDR sepeti boş — ana sayfadan sepete kayıt ekleyin"
          }
          onClick={() => {
            if (!canPickFromBasket) {
              toast.warning("CDR sepeti boş. Ana sayfadaki çağrı kayıtlarından sepete ekleyin.");
              return;
            }
            onOpenPicker(pickerContext, col.key, row);
          }}
        >
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onFieldChange(col.key, e.target.value)}
      className={cellInput}
    />
  );
};

const TlRecordingTableScrollArea = ({ isDark, children }) => {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [metrics, setMetrics] = useState({
    left: 0, max: 0, track: 0, scrollWidth: 0, clientWidth: 0,
  });

  const readMetrics = useCallback(() => {
    const el = scrollRef.current;
    const track = trackRef.current;
    if (!el) return;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    setMetrics({
      left: el.scrollLeft,
      max,
      track: track?.clientWidth || el.clientWidth,
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
    });
  }, []);

  const setTrackNode = useCallback((node) => {
    trackRef.current = node;
    readMetrics();
  }, [readMetrics]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    readMetrics();
    const timers = [
      setTimeout(readMetrics, 0),
      setTimeout(readMetrics, 120),
      setTimeout(readMetrics, 400),
    ];

    el.addEventListener("scroll", readMetrics, { passive: true });
    const observer = new ResizeObserver(readMetrics);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    window.addEventListener("resize", readMetrics);
    return () => {
      timers.forEach(clearTimeout);
      el.removeEventListener("scroll", readMetrics);
      observer.disconnect();
      window.removeEventListener("resize", readMetrics);
    };
  }, [children, readMetrics]);

  const canScroll = metrics.max > 4;
  const trackWidth = Math.max(metrics.track || 1, 1);
  const thumbWidth = canScroll
    ? Math.max(56, Math.round((metrics.clientWidth / metrics.scrollWidth) * trackWidth))
    : trackWidth;
  const thumbTravel = Math.max(0, trackWidth - thumbWidth);
  const thumbLeft = canScroll && metrics.max > 0
    ? Math.round((metrics.left / metrics.max) * thumbTravel)
    : 0;

  const setScrollLeft = useCallback((nextLeft) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = Math.min(metrics.max, Math.max(0, nextLeft));
    readMetrics();
  }, [metrics.max, readMetrics]);

  const scrollToRatio = useCallback((ratio) => {
    if (metrics.max <= 0 || thumbTravel <= 0) return;
    const clamped = Math.min(1, Math.max(0, ratio));
    setScrollLeft(clamped * metrics.max);
  }, [metrics.max, setScrollLeft, thumbTravel]);

  const scrollBy = useCallback((delta) => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollLeft(el.scrollLeft + delta);
  }, [setScrollLeft]);

  const handleTrackPointerDown = (event) => {
    if (!canScroll || !trackRef.current || thumbTravel <= 0) return;
    if (event.target !== trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    scrollToRatio((clickX - thumbWidth / 2) / thumbTravel);
  };

  const handleThumbPointerDown = (event) => {
    if (!canScroll || thumbTravel <= 0) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      startX: event.clientX,
      startScroll: metrics.left,
      max: metrics.max,
      thumbTravel,
    };

    const handleMove = (moveEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.thumbTravel <= 0) return;
      const deltaX = moveEvent.clientX - drag.startX;
      const deltaScroll = (deltaX / drag.thumbTravel) * drag.max;
      setScrollLeft(drag.startScroll + deltaScroll);
    };

    const handleUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  };

  const tone = isDark ? "tl-recording-table-scroll--dark" : "tl-recording-table-scroll--light";

  return (
    <div className="tl-recording-table-scroll-wrap">
      <div className={`tl-recording-scroll-bar ${tone}`}>
        <span className="tl-recording-scroll-label">Sütunları kaydır</span>
        <button
          type="button"
          className="tl-recording-scroll-btn"
          disabled={!canScroll || metrics.left <= 0}
          onClick={() => scrollBy(-280)}
          aria-label="Sola kaydır"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div
          ref={setTrackNode}
          className={`tl-recording-scroll-track ${canScroll ? "" : "tl-recording-scroll-track--disabled"}`}
          onPointerDown={handleTrackPointerDown}
          role="scrollbar"
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={metrics.max}
          aria-valuenow={metrics.left}
          aria-label="Tablo yatay kaydırma"
        >
          <div
            className="tl-recording-scroll-thumb"
            style={{ width: `${thumbWidth}px`, transform: `translateX(${thumbLeft}px)` }}
            onPointerDown={handleThumbPointerDown}
          />
        </div>
        <button
          type="button"
          className="tl-recording-scroll-btn"
          disabled={!canScroll || metrics.left >= metrics.max - 1}
          onClick={() => scrollBy(280)}
          aria-label="Sağa kaydır"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div ref={scrollRef} className={`tl-recording-table-scroll ${tone}`}>
        <table className="panel-table caption-bottom text-sm w-max min-w-full border-collapse">
          {children}
        </table>
      </div>
    </div>
  );
};

const TlRecordingSpreadsheetTable = ({
  items, draftRows, editingId, editBuffer, rowSavingId, approvalSavingId, canEditApprovals,
  canCreateKaliteReport = false,
  searchableAgents, isDark, textClass, labelClass,
  inputClass, apiBase, onAddRow, onUpdateDraft, onRemoveDraft, onSaveDraft, onStartEdit, onCancelEdit,
  onUpdateEdit, onSaveEdit, onDelete, onOpenDetail, onOpenPicker, onPlayRecording, onSaveApproval,
  onCreateKaliteReport,
  cdrBasketCount = 0, contextIsoYear,
}) => {
  const tabBarClass = isDark
    ? "border-slate-800 bg-slate-950/80"
    : "border-gray-200 bg-gradient-to-b from-gray-50 to-white";
  const tabActiveClass = isDark
    ? "border-slate-700 bg-slate-900 text-slate-200"
    : "border-gray-200 bg-white text-gray-700 shadow-sm";
  const addTabClass = isDark
    ? "border-slate-700 text-slate-400 hover:text-violet-300 hover:border-violet-500/50 hover:bg-violet-500/10"
    : "border-gray-200 text-gray-400 hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50";

  const actionBtn = (extra = "") => `h-8 w-8 p-0 rounded-lg ${extra}`;

  const renderRowActions = (kind, row, isEditing) => {
    const saving = rowSavingId === (kind === "draft" ? row._draftId : row.id);
    if (kind === "draft" || isEditing) {
      return (
        <PanelTableActions>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={`${actionBtn()} ${isDark ? "text-emerald-400 hover:bg-emerald-500/10" : "text-emerald-600 hover:bg-emerald-50"}`}
            title="Kaydet"
            disabled={saving}
            onClick={() => (kind === "draft" ? onSaveDraft(row._draftId) : onSaveEdit())}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={`${actionBtn()} ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-100"}`}
            title="İptal"
            disabled={saving}
            onClick={() => (kind === "draft" ? onRemoveDraft(row._draftId) : onCancelEdit())}
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </PanelTableActions>
      );
    }
    return (
      <PanelTableActions>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`${actionBtn()} ${isDark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-gray-100 text-gray-500"}`}
          title={formatAuditMetaLine(row) || "Detay — giriş ve güncelleme bilgisi burada"}
          onClick={() => onOpenDetail(row)}
        >
          <Eye className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" size="sm" variant="ghost" className={`${actionBtn()} ${isDark ? "hover:bg-slate-800 text-blue-400" : "hover:bg-blue-50 text-blue-600"}`} title="Düzenle" onClick={() => onStartEdit(row)}>
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        {canCreateKaliteReport && onCreateKaliteReport ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={`${actionBtn()} ${isDark ? "hover:bg-emerald-500/10 text-emerald-400" : "hover:bg-emerald-50 text-emerald-700"}`}
            title="Kalite raporu oluştur (ses kayıtları aktarılır)"
            onClick={() => onCreateKaliteReport(row)}
          >
            <ClipboardList className="w-3.5 h-3.5" />
          </Button>
        ) : null}
        <Button type="button" size="sm" variant="ghost" className={`${actionBtn()} text-rose-500 ${isDark ? "hover:bg-rose-500/10" : "hover:bg-rose-50"}`} title="Sil" onClick={() => onDelete(row)}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </PanelTableActions>
    );
  };

  const rowAccent = (kind, isEditing) => {
    if (kind === "draft") {
      return isDark
        ? "border-l-[3px] border-l-violet-500 bg-violet-950/25 hover:bg-violet-950/35"
        : "border-l-[3px] border-l-violet-500 bg-violet-50/70 hover:bg-violet-50";
    }
    if (isEditing) {
      return isDark
        ? "border-l-[3px] border-l-amber-500 bg-amber-950/15 hover:bg-amber-950/25"
        : "border-l-[3px] border-l-amber-400 bg-amber-50/60 hover:bg-amber-50/80";
    }
    return undefined;
  };

  const empty = draftRows.length === 0 && items.length === 0;
  const totalRows = draftRows.length + items.length;

  return (
    <div>
      <div className={`flex items-end gap-1.5 px-3 sm:px-4 pt-3 border-b ${tabBarClass}`}>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-t-xl border border-b-0 text-xs font-medium ${tabActiveClass}`}>
          <FileAudio className="w-3.5 h-3.5 text-violet-500 shrink-0" />
          <span>Kayıtlar</span>
          <Badge variant="secondary" className={`h-5 px-1.5 text-[10px] font-mono tabular-nums ${isDark ? "bg-slate-800 text-slate-300" : ""}`}>
            {totalRows}
          </Badge>
        </div>
        {draftRows.length > 0 && (
          <div className={`px-3 py-2 rounded-t-xl border border-b-0 text-xs font-medium ${isDark ? "border-violet-500/40 bg-violet-500/10 text-violet-300" : "border-violet-200 bg-violet-50 text-violet-700"}`}>
            {draftRows.length} taslak
          </div>
        )}
        <button
          type="button"
          onClick={onAddRow}
          title="Yeni satır ekle"
          className={`flex items-center justify-center w-9 h-[34px] rounded-t-xl border border-b-0 transition-colors ${addTabClass}`}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <TlRecordingTableScrollArea isDark={isDark}>
        <TableHeader>
          <PanelTableHeadRow isDark={isDark}>
            {TL_TABLE_COLUMNS.map((col) => {
              const meta = colMeta(col.key);
              return (
                <PanelTableHead
                  key={col.key}
                  isDark={isDark}
                  align={meta.align || "left"}
                  className={`py-2.5 align-bottom px-2 ${getTlCellWidthClass(meta)} ${meta.head || ""} ${meta.narrow ? "px-1" : ""} ${meta.compact ? "px-0.5" : ""}`}
                >
                  <TlRecordingTableHeadLabel
                    lines={TL_COLUMN_HEAD_LINES[col.key] || [col.label]}
                    align={meta.align || "left"}
                  />
                </PanelTableHead>
              );
            })}
            <PanelTableHead isDark={isDark} className="w-[9.5rem] sticky right-0 z-20 text-center py-2.5 align-bottom">
              <TlRecordingTableHeadLabel lines={["İşlemler"]} align="center" />
            </PanelTableHead>
          </PanelTableHeadRow>
        </TableHeader>
        <PanelTableBody>
          {empty ? (
            <PanelTableEmpty
              colSpan={TL_TABLE_COLUMNS.length + 1}
              isDark={isDark}
              icon={FileAudio}
              title="Henüz kayıt yok"
              description="Sol üstteki + ile yeni satır ekleyin"
            />
          ) : null}

          {draftRows.map((row) => (
            <PanelTableRow key={row._draftId} isDark={isDark} className={rowAccent("draft", false)}>
              {TL_TABLE_COLUMNS.map((col) => {
                const meta = colMeta(col.key);
                return (
                  <PanelTableCell
                    key={col.key}
                    isDark={isDark}
                    align={meta.align || "left"}
                    className={`text-xs align-top py-3 px-1 ${getTlCellWidthClass(meta)} ${meta.narrow ? "min-w-[160px]" : ""} ${meta.compact ? "px-0.5" : ""} ${col.key === "assistant_name" ? "sticky left-0 z-10 " + (isDark ? "bg-violet-950/25" : "bg-violet-50/70") : ""}`}
                  >
                    {col.type === "approval" ? (
                      <span className={`text-[11px] ${isDark ? "text-slate-500" : "text-gray-400"}`}>—</span>
                    ) : (
                      <TlRecordingInlineCell
                        col={col}
                        row={row}
                        isEditing
                        onFieldChange={(key, val) => onUpdateDraft(row._draftId, key, val)}
                        onFieldsChange={(fields) => onUpdateDraft(row._draftId, null, fields)}
                        searchableAgents={searchableAgents}
                        isDark={isDark}
                        inputClass={inputClass}
                        labelClass={labelClass}
                        textClass={textClass}
                        apiBase={apiBase}
                        onOpenPicker={onOpenPicker}
                        onOpenDetail={onOpenDetail}
                        onPlayRecording={onPlayRecording}
                        cdrBasketCount={cdrBasketCount}
                        contextIsoYear={contextIsoYear}
                        pickerContext={{ mode: "draft", draftId: row._draftId }}
                      />
                    )}
                  </PanelTableCell>
                );
              })}
              <PanelTableCell isDark={isDark} className={`sticky right-0 align-top ${isDark ? "bg-violet-950/25" : "bg-violet-50/70"}`}>
                {renderRowActions("draft", row, true)}
              </PanelTableCell>
            </PanelTableRow>
          ))}

          {items.map((row, idx) => {
            const isEditing = editingId === row.id;
            const displayRow = isEditing ? editBuffer : row;
            const stickyBg = isEditing
              ? (isDark ? "bg-amber-950/15" : "bg-amber-50/60")
              : (idx % 2 === 1 ? (isDark ? "bg-slate-900/25" : "bg-gray-50/40") : (isDark ? "bg-slate-950/20" : "bg-white"));
            const zebra = !isEditing && idx % 2 === 1
              ? (isDark ? "bg-slate-900/25" : "bg-gray-50/40")
              : undefined;
            return (
              <PanelTableRow key={row.id} isDark={isDark} className={rowAccent("saved", isEditing) || zebra}>
                {TL_TABLE_COLUMNS.map((col) => {
                  const meta = colMeta(col.key);
                  return (
                    <PanelTableCell
                      key={col.key}
                      isDark={isDark}
                      align={meta.align || "left"}
                      className={`text-xs align-top py-3 px-1 ${getTlCellWidthClass(meta)} ${meta.narrow ? (isEditing ? "min-w-[160px]" : "") : ""} ${meta.compact ? "px-0.5" : ""} ${col.key === "assistant_name" ? `sticky left-0 z-10 ${stickyBg}` : ""}`}
                    >
                      {col.type === "approval" ? (
                        <TlOnaySelectCell
                          row={row}
                          field={col.key}
                          canEdit={canEditApprovals}
                          isDark={isDark}
                          inputClass={inputClass}
                          onSave={onSaveApproval}
                          savingId={approvalSavingId}
                        />
                      ) : (
                        <TlRecordingInlineCell
                          col={col}
                          row={displayRow}
                          isEditing={isEditing}
                          onFieldChange={(key, val) => onUpdateEdit(key, val)}
                          onFieldsChange={(fields) => onUpdateEdit(null, fields)}
                          searchableAgents={searchableAgents}
                          isDark={isDark}
                          inputClass={inputClass}
                          labelClass={labelClass}
                          textClass={textClass}
                          apiBase={apiBase}
                          onOpenPicker={onOpenPicker}
                          onOpenDetail={onOpenDetail}
                          onPlayRecording={onPlayRecording}
                          cdrBasketCount={cdrBasketCount}
                          contextIsoYear={contextIsoYear}
                          pickerContext={{ mode: "inline", editingId: row.id }}
                        />
                      )}
                    </PanelTableCell>
                  );
                })}
                <PanelTableCell isDark={isDark} className={`sticky right-0 align-top ${isEditing ? (isDark ? "bg-amber-950/15" : "bg-amber-50/60") : stickyBg}`}>
                  {renderRowActions("saved", row, isEditing)}
                </PanelTableCell>
              </PanelTableRow>
            );
          })}
        </PanelTableBody>
      </TlRecordingTableScrollArea>
    </div>
  );
};

const TEXT_PREVIEW_FIELDS = [
  { key: "assistant_report", label: "Asistan Ses Kaydı Raporu" },
  { key: "leader_report", label: "Tk Lideri Ses Kaydı Raporu" },
];

const TlRecordingActivityLogDialog = ({
  open, onClose, apiBase, isDark, textClass, labelClass, reportIdFilter, refreshKey = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const panelClass = isDark ? "bg-slate-900 border-slate-700 text-white" : "";
  const rowClass = isDark ? "border-slate-800 bg-slate-950/30" : "border-gray-100 bg-gray-50/80";
  const totalPages = Math.max(1, Math.ceil(total / ACTIVITY_LOG_PAGE_SIZE));

  const loadLog = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/tl-recording-reports`, {
        params: {
          action: "activity-log",
          limit: ACTIVITY_LOG_PAGE_SIZE,
          offset: (page - 1) * ACTIVITY_LOG_PAGE_SIZE,
          report_id: reportIdFilter || undefined,
        },
      });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "İşlem geçmişi yüklenemedi");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [apiBase, open, page, reportIdFilter]);

  useEffect(() => {
    if (open) loadLog();
  }, [open, loadLog, refreshKey]);

  useEffect(() => {
    if (open) setPage(1);
  }, [open, reportIdFilter]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`max-w-4xl max-h-[85vh] overflow-hidden flex flex-col ${panelClass}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-violet-500" />
            İşlem geçmişi
          </DialogTitle>
          <p className={`text-xs font-normal ${labelClass}`}>
            {reportIdFilter
              ? "Bu kayıt için yapılan işlemler (kim, ne zaman, ne yaptı)."
              : "Tk. lideri ses kayıtları sayfasında yapılan ekleme, güncelleme, onay ve silme işlemleri."}
          </p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto min-h-0 space-y-2 py-1">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-violet-500" /></div>
          ) : items.length === 0 ? (
            <p className={`text-sm text-center py-10 ${labelClass}`}>Henüz kayıtlı işlem yok. Yeni işlemler burada listelenir.</p>
          ) : (
            items.map((ev) => (
              <div key={ev.id} className={`rounded-lg border px-3 py-2.5 text-xs ${rowClass}`}>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className={`font-semibold ${textClass}`}>{ev.user_name || "—"}</span>
                  <Badge variant="outline" className="text-[10px] h-5">
                    {TL_ACTIVITY_ACTION_LABELS[ev.action] || ev.action}
                  </Badge>
                  <span className={labelClass}>{formatActivityTimestamp(ev.created_at)}</span>
                </div>
                {(ev.assistant_name || ev.report_date) && (
                  <p className={`mt-1 ${labelClass}`}>
                    {ev.assistant_name ? ev.assistant_name : ""}
                    {ev.report_date ? ` · ${formatReportDate(ev.report_date)}` : ""}
                    {ev.report_id ? ` · #${ev.report_id}` : ""}
                  </p>
                )}
                {ev.detail ? (
                  <p className={`mt-1.5 leading-relaxed ${isDark ? "text-slate-300" : "text-gray-700"}`}>{ev.detail}</p>
                ) : null}
              </div>
            ))
          )}
        </div>
        {total > ACTIVITY_LOG_PAGE_SIZE && (
          <div className="flex items-center justify-between pt-2 border-t border-dashed">
            <span className={`text-[11px] ${labelClass}`}>{total} işlem</span>
            <div className="flex gap-1">
              <Button type="button" size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Önceki</Button>
              <span className={`text-xs px-2 self-center ${labelClass}`}>{page} / {totalPages}</span>
              <Button type="button" size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>Sonraki</Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}><X className="w-4 h-4 mr-1" /> Kapat</Button>
          <Button type="button" variant="outline" onClick={loadLog} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Yenile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TlRecordingDetailModal = ({
  open, row, focusField = null, onClose, onEdit, isDark, textClass, labelClass, apiBase, onPlayRecording, onShowActivityLog,
}) => {
  if (!row) return null;
  const panelClass = isDark ? "bg-slate-900 border-slate-700 text-white" : "";
  const blockClass = isDark ? "border-slate-700 bg-slate-950/40" : "border-gray-200 bg-gray-50";
  const focusMeta = focusField ? TEXT_PREVIEW_FIELDS.find((f) => f.key === focusField) : null;
  const visibleTextFields = focusField
    ? TEXT_PREVIEW_FIELDS.filter((f) => f.key === focusField)
    : TEXT_PREVIEW_FIELDS;
  const showRecordingUrls = !focusField;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`max-w-3xl max-h-[90vh] overflow-y-auto ${panelClass}`}>
        <DialogHeader>
          <DialogTitle className="pr-8">
            {focusMeta ? focusMeta.label : row.assistant_name}
            <span className={`block text-sm font-normal mt-1 ${labelClass}`}>
              {focusMeta ? row.assistant_name : null}
              {focusMeta && " · "}
              {formatReportDate(row.report_date)}
              {row.week_number ? (
                <>
                  {" · "}
                  {formatWeekRangeLabel(
                    resolveIsoYearForRow(row, getCurrentIsoWeekContext().isoYear),
                    row.week_number,
                  )}
                </>
              ) : ""}
              {row.leader_name ? ` · ${row.leader_name}` : ""}
            </span>
          </DialogTitle>
        </DialogHeader>

        {!focusField && (row.created_at || row.updated_at) ? (
          <div className={`rounded-lg border px-3 py-2.5 text-[11px] leading-relaxed ${blockClass}`}>
            <p className={`font-semibold mb-1 ${textClass}`}>Kayıt bilgisi</p>
            <p className={labelClass}>
              <span className="block">Giriş: {row.created_by_name || "—"} · {formatActivityTimestamp(row.created_at)}</span>
              {row.updated_at && row.updated_at !== row.created_at ? (
                <span className="block mt-1">Son güncelleme: {row.updated_by_name || "—"} · {formatActivityTimestamp(row.updated_at)}</span>
              ) : (
                <span className="block mt-1 text-[10px] opacity-80">Henüz güncellenmemiş (veya sadece oluşturulmuş).</span>
              )}
            </p>
          </div>
        ) : null}

        <div className="space-y-3 py-1">
          {visibleTextFields.map((field) => {
            const value = String(row[field.key] || "").trim();
            if (!value) {
              if (focusField) {
                return (
                  <p key={field.key} className={`text-sm text-center py-6 ${labelClass}`}>Bu alanda metin yok</p>
                );
              }
              return null;
            }
            return (
              <div key={field.key} className={`rounded-lg border p-3 ${blockClass}`}>
                {!focusField && (
                  <div className={`text-xs font-semibold mb-2 ${textClass}`}>{field.label}</div>
                )}
                <div className={`text-sm whitespace-pre-wrap break-words leading-relaxed ${textClass}`}>{value}</div>
              </div>
            );
          })}

          {!focusField && !hasReportText(row) && (
            <p className={`text-sm text-center py-6 ${labelClass}`}>Bu kayıtta rapor metni yok</p>
          )}

          {showRecordingUrls && RECORDING_URL_FIELDS.map((field) => {
            const value = String(row[field.key] || "").trim();
            if (!value) return null;
            return (
              <div key={field.key} className={`rounded-lg border p-3 ${blockClass}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className={`text-xs font-semibold ${textClass}`}>{field.label}</div>
                  <TlRecordingUrlActions
                    url={value}
                    apiBase={apiBase}
                    isDark={isDark}
                    labelClass={labelClass}
                    compact={false}
                    onPlay={(recordingUrl) => onPlayRecording(recordingUrl, `${field.label} · ${row.assistant_name || ""}`)}
                  />
                </div>
                <p className={`text-[11px] font-mono break-all ${labelClass}`}>{value}</p>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {onShowActivityLog && row.id ? (
            <Button type="button" variant="outline" onClick={() => onShowActivityLog(row.id)}>
              <History className="w-4 h-4 mr-1" /> Bu kaydın geçmişi
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={onClose}><X className="w-4 h-4 mr-1" /> Kapat</Button>
          <Button type="button" onClick={() => { onEdit(row); onClose(); }}>
            <Pencil className="w-4 h-4 mr-1" /> Düzenle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TeamLeaderRecordingReportsPage = ({
  apiBase, isDark, textClass, labelClass, isAdmin, canEditApprovals = false, canCreateKaliteReport = false,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [assistantFilter, setAssistantFilter] = useState(null);
  const [listFilterMode, setListFilterMode] = useState(REPORT_LIST_FILTER_ALL);
  const [filterIsoYear, setFilterIsoYear] = useState(() => getCurrentIsoWeekContext().isoYear);
  const [filterWeek, setFilterWeek] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [agents, setAgents] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [leaderFilter, setLeaderFilter] = useState("");
  const [draftRows, setDraftRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editBuffer, setEditBuffer] = useState(EMPTY_TL_RECORDING_FORM);
  const [rowSavingId, setRowSavingId] = useState(null);
  const [approvalSavingId, setApprovalSavingId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [detailFocusField, setDetailFocusField] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [audioPreview, setAudioPreview] = useState({ open: false, url: "", title: "", recordInfo: null });
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [activityLogReportId, setActivityLogReportId] = useState(null);
  const [activityLogRefreshKey, setActivityLogRefreshKey] = useState(0);
  const cdrStaging = useCdrStagingBasket();

  const bumpActivityLog = useCallback(() => {
    setActivityLogRefreshKey((k) => k + 1);
  }, []);

  const openActivityLog = useCallback((reportId = null) => {
    setActivityLogReportId(reportId);
    setActivityLogOpen(true);
  }, []);

  const assistantSearch = assistantFilter?.ad_soyad?.trim() || "";

  const openDetail = useCallback((row, focusField = null) => {
    setDetailRow(row);
    setDetailFocusField(focusField);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailRow(null);
    setDetailFocusField(null);
  }, []);

  const openRecordingPlayer = useCallback((url, title = "Ses kaydı dinle", extra = {}) => {
    const recordingUrl = String(url || "").trim();
    if (!recordingUrl) {
      toast.warning("Ses URL yok");
      return;
    }
    setAudioPreview({
      open: true,
      url: recordingUrl,
      title,
      recordInfo: buildReportRecordingInfo({
        title,
        url: recordingUrl,
        sourceLabel: "Tk. Liderleri Ses Kayıtları Raporu",
        ...extra,
      }),
    });
  }, []);

  const panelClass = isDark ? "border-slate-800 bg-slate-900/60" : "border-gray-200 bg-white";
  const inputClass = isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-gray-200";
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/tl-recording-reports`, { params: { action: "meta" } });
      setAgents(res.data?.agents || []);
      setLeaders(res.data?.leaders || []);
    } catch {
      /* ignore */
    }
  }, [apiBase]);

  const tlListQueryParams = useMemo(() => ({
    search: assistantSearch || undefined,
    leader_user_id: leaderFilter || undefined,
    ...buildReportListWeekDateParams({
      filterMode: listFilterMode,
      filterWeek,
      startDate,
      endDate,
    }),
  }), [assistantSearch, leaderFilter, listFilterMode, filterWeek, startDate, endDate]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/tl-recording-reports`, {
        params: {
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          ...tlListQueryParams,
        },
      });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rapor yüklenemedi");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [apiBase, page, tlListQueryParams]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const searchableAgents = useMemo(() => agentsForSearchSelect(agents), [agents]);

  const addNewRow = useCallback(() => {
    const ctx = getCurrentIsoWeekContext();
    const week = resolveLastWeekNumber(draftRows, items) || filterWeek || ctx.week;
    const year = filterWeek ? filterIsoYear : ctx.isoYear;
    const range = getIsoWeekRange(year, week);
    setDraftRows((prev) => [createDraftRow({
      week_number: week,
      report_date: range.start,
    }), ...prev]);
  }, [draftRows, items, filterWeek, filterIsoYear]);

  const updateDraftRow = useCallback((draftId, key, valueOrFields) => {
    setDraftRows((prev) => prev.map((row) => {
      if (row._draftId !== draftId) return row;
      if (key === null && valueOrFields && typeof valueOrFields === "object") {
        return { ...row, ...valueOrFields };
      }
      return { ...row, [key]: valueOrFields };
    }));
  }, []);

  const removeDraftRow = useCallback((draftId) => {
    setDraftRows((prev) => prev.filter((row) => row._draftId !== draftId));
  }, []);

  const saveDraftRow = async (draftId) => {
    const row = draftRows.find((r) => r._draftId === draftId);
    if (!row) return;
    if (!row.assistant_name?.trim() || !row.report_date) {
      toast.error("Asistan ve tarih zorunlu");
      return;
    }
    setRowSavingId(draftId);
    try {
      await axios.post(`${apiBase}/tl-recording-reports`, formRowToPayload(row));
      toast.success("Kayıt eklendi");
      setDraftRows((prev) => prev.filter((r) => r._draftId !== draftId));
      loadItems();
      loadMeta();
      bumpActivityLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setRowSavingId(null);
    }
  };

  const startEdit = (row) => {
    if (editingId && editingId !== row.id) {
      toast.warning("Önce açık düzenlemeyi kaydedin veya iptal edin");
      return;
    }
    setEditingId(row.id);
    setEditBuffer(mapItemToForm(row));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBuffer(EMPTY_TL_RECORDING_FORM);
  };

  const updateEditRow = useCallback((key, valueOrFields) => {
    setEditBuffer((prev) => {
      if (key === null && valueOrFields && typeof valueOrFields === "object") {
        return { ...prev, ...valueOrFields };
      }
      return { ...prev, [key]: valueOrFields };
    });
  }, []);

  const saveEditRow = async () => {
    if (!editingId) return;
    if (!editBuffer.assistant_name?.trim() || !editBuffer.report_date) {
      toast.error("Asistan ve tarih zorunlu");
      return;
    }
    setRowSavingId(editingId);
    try {
      await axios.put(`${apiBase}/tl-recording-reports?id=${editingId}`, formRowToPayload(editBuffer));
      toast.success("Kayıt güncellendi");
      setEditingId(null);
      setEditBuffer(EMPTY_TL_RECORDING_FORM);
      loadItems();
      loadMeta();
      bumpActivityLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setRowSavingId(null);
    }
  };

  const saveApproval = async (id, field, value) => {
    const current = items.find((r) => r.id === id);
    if (!current) return;
    const approvalFields = [
      "ask_onay", "tksk_onay", "ordeleme", "mr_isteme", "mr_tepinme", "tedirginlik", "uzmanlik",
    ];
    const payload = Object.fromEntries(
      approvalFields.map((key) => [key, field === key ? value : (current[key] || "")]),
    );
    setApprovalSavingId(id);
    try {
      const res = await axios.put(`${apiBase}/tl-recording-reports?id=${id}&approvals=1`, payload);
      const item = res.data?.item;
      if (item) {
        setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...item } : r)));
      } else {
        loadItems();
      }
      bumpActivityLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Onay güncellenemedi");
    } finally {
      setApprovalSavingId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.assistant_name}" kaydı silinsin mi?`)) return;
    try {
      await axios.delete(`${apiBase}/tl-recording-reports?id=${row.id}`);
      toast.success("Silindi");
      loadItems();
      bumpActivityLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  const openInlinePicker = (context, fieldKey, row) => {
    if (cdrStaging.count <= 0) {
      toast.warning("CDR sepeti boş. Ana sayfadaki çağrı kayıtlarından sepete ekleyin.");
      return;
    }
    setPickerTarget({
      ...context,
      fieldKey,
      agentId: row.agent_id,
      reportDate: row.report_date,
    });
    setPickerOpen(true);
  };

  const handleCreateKaliteReport = useCallback(async (row) => {
    if (!row?.id) return;
    if (!row.agent_id || !row.leader_user_id) {
      toast.error("Kalite aktarımı için asistan ve takım lideri eşlemesi gerekli");
      return;
    }
    const call1 = String(row.call1_recording_url || "").trim();
    const call2 = String(row.call2_recording_url || "").trim();
    if (!call1 && !call2) {
      toast.error("1. veya 2. görüşme ses kaydı URL’si olmalı");
      return;
    }
    const reportDate = row.report_date ? String(row.report_date).slice(0, 10) : "";
    if (reportDate) {
      try {
        const res = await axios.get(`${apiBase}/manager-quality-reports`, {
          params: {
            report_type: "kalite-degerlendirme",
            agent_id: row.agent_id,
            start_date: reportDate,
            end_date: reportDate,
            limit: 1,
          },
        });
        if ((res.data?.total || 0) > 0) {
          const ok = window.confirm(
            "Bu asistan ve tarih için zaten kalite değerlendirme kaydı var. Yine de yeni taslak açılsın mı?",
          );
          if (!ok) return;
        }
      } catch {
        /* devam */
      }
    }
    if (!storeKalitePrefillFromTl(row)) {
      toast.error("Taslak hazırlanamadı");
      return;
    }
    toast.success("Kalite değerlendirme sayfasında taslak açılıyor…");
    navigate("/reports?view=mgr-kalite-degerlendirme");
  }, [apiBase, navigate]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${apiBase}/tl-recording-reports`, {
        params: {
          action: "export",
          ...tlListQueryParams,
        },
        timeout: 120000,
      });
      const sheets = res.data?.sheets || {};
      const sheetEntries = Object.entries(sheets);
      if (sheetEntries.length === 0) {
        toast.warning("Dışa aktarılacak kayıt yok");
        return;
      }

      const wb = XLSX.utils.book_new();
      const usedNames = new Set();
      sheetEntries.forEach(([leaderName, rows]) => {
        let sheetName = sanitizeExcelSheetName(leaderName);
        let suffix = 2;
        while (usedNames.has(sheetName)) {
          const base = sanitizeExcelSheetName(leaderName).slice(0, 28);
          sheetName = `${base} ${suffix}`;
          suffix += 1;
        }
        usedNames.add(sheetName);
        const ws = XLSX.utils.aoa_to_sheet(buildExcelRows(rows));
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Tk-Liderleri-Ses-Kayitlari-${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${res.data?.total || 0} kayıt Excel'e aktarıldı`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Excel dışa aktarılamadı");
    } finally {
      setExporting(false);
    }
  };

  const agentOptions = useMemo(() => agents, [agents]);

  return (
    <div className={`rounded-2xl border overflow-hidden ${panelClass}`}>
      <div className={`px-4 py-4 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 ${isDark ? "border-slate-800" : "border-gray-100"}`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDark ? "bg-violet-500/15 text-violet-400" : "bg-violet-50 text-violet-600"}`}>
            <FileAudio className="w-5 h-5" />
          </div>
          <div>
            <h3 className={`text-base font-semibold ${textClass}`}>Tk. Liderleri Ses Kayıtları Raporu</h3>
            <p className={`text-xs ${labelClass}`}>Takım lideri günlük asistan ses kaydı değerlendirme formu</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={handleExport} disabled={exporting || loading} className={isDark ? "border-slate-700" : ""}>
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            Excel
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => openActivityLog(null)} className={isDark ? "border-slate-700" : ""}>
            <History className="w-4 h-4 mr-1" /> İşlem geçmişi
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={loadItems} disabled={loading} className={isDark ? "border-slate-700" : ""}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className={`px-4 py-3 border-b flex flex-col xl:flex-row gap-2 ${isDark ? "border-slate-800 bg-slate-950/20" : "border-gray-100 bg-gray-50/70"}`}>
        <div className="min-w-[200px] flex-1 max-w-xs">
          <SearchableAgentSelect
            agents={searchableAgents}
            value={assistantFilter}
            onChange={(agent) => { setAssistantFilter(agent); setPage(1); }}
            placeholder="Asistan ara…"
            isDark={isDark}
            inputClass={inputClass}
            labelClass={labelClass}
          />
        </div>
        <TlIsoWeekPicker
          isoYear={filterIsoYear}
          week={listFilterMode === REPORT_LIST_FILTER_WEEK ? filterWeek : ""}
          isDark={isDark}
          inputClass={inputClass}
          onChange={(payload) => {
            if (payload.cleared) {
              setListFilterMode(REPORT_LIST_FILTER_ALL);
              setFilterWeek("");
              setStartDate("");
              setEndDate("");
              setPage(1);
              return;
            }
            setListFilterMode(REPORT_LIST_FILTER_WEEK);
            setFilterIsoYear(payload.isoYear);
            setFilterWeek(payload.week);
            setStartDate("");
            setEndDate("");
            setPage(1);
          }}
        />
        <Input
          type="date"
          value={listFilterMode === REPORT_LIST_FILTER_DATE ? startDate : ""}
          onChange={(e) => {
            const v = e.target.value;
            setStartDate(v);
            if (v || endDate) {
              setListFilterMode(REPORT_LIST_FILTER_DATE);
              setFilterWeek("");
            } else if (!v && !endDate) {
              setListFilterMode(REPORT_LIST_FILTER_ALL);
            }
            setPage(1);
          }}
          className={`h-9 md:w-36 ${inputClass}`}
          title="Başlangıç tarihi"
        />
        <Input
          type="date"
          value={listFilterMode === REPORT_LIST_FILTER_DATE ? endDate : ""}
          onChange={(e) => {
            const v = e.target.value;
            setEndDate(v);
            if (startDate || v) {
              setListFilterMode(REPORT_LIST_FILTER_DATE);
              setFilterWeek("");
            } else if (!v && !startDate) {
              setListFilterMode(REPORT_LIST_FILTER_ALL);
            }
            setPage(1);
          }}
          className={`h-9 md:w-36 ${inputClass}`}
          title="Bitiş tarihi"
        />
        {isAdmin && leaders.length > 0 && (
          <select value={leaderFilter} onChange={(e) => { setLeaderFilter(e.target.value); setPage(1); }} className={`h-9 rounded-md border px-2 text-sm min-w-[180px] ${inputClass}`}>
            <option value="">Tüm liderler</option>
            {leaders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
        <Button type="button" size="sm" className="h-9" onClick={() => { setPage(1); loadItems(); }}>Filtrele</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>
      ) : (
        <TlRecordingSpreadsheetTable
          items={items}
          draftRows={draftRows}
          editingId={editingId}
          editBuffer={editBuffer}
          rowSavingId={rowSavingId}
          approvalSavingId={approvalSavingId}
          canEditApprovals={canEditApprovals}
          canCreateKaliteReport={canCreateKaliteReport}
          onCreateKaliteReport={handleCreateKaliteReport}
          onSaveApproval={saveApproval}
          searchableAgents={searchableAgents}
          isDark={isDark}
          textClass={textClass}
          labelClass={labelClass}
          inputClass={inputClass}
          apiBase={apiBase}
          onAddRow={addNewRow}
          onUpdateDraft={updateDraftRow}
          onRemoveDraft={removeDraftRow}
          onSaveDraft={saveDraftRow}
          onStartEdit={startEdit}
          onCancelEdit={cancelEdit}
          onUpdateEdit={updateEditRow}
          onSaveEdit={saveEditRow}
          onDelete={handleDelete}
          onOpenDetail={openDetail}
          onOpenPicker={openInlinePicker}
          onPlayRecording={openRecordingPlayer}
          cdrBasketCount={cdrStaging.count}
          contextIsoYear={filterIsoYear}
        />
      )}

      <CdrTablePagination isDark={isDark} page={page} pageSize={PAGE_SIZE} total={total} totalPages={totalPages} loading={loading} onPageChange={setPage} />

      <TlRecordingDetailModal
        open={Boolean(detailRow)}
        row={detailRow}
        focusField={detailFocusField}
        onClose={closeDetail}
        onEdit={(row) => { closeDetail(); startEdit(row); }}
        onShowActivityLog={(id) => { closeDetail(); openActivityLog(id); }}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        apiBase={apiBase}
        onPlayRecording={openRecordingPlayer}
      />

      <TlRecordingActivityLogDialog
        open={activityLogOpen}
        onClose={() => { setActivityLogOpen(false); setActivityLogReportId(null); }}
        apiBase={apiBase}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        reportIdFilter={activityLogReportId}
        refreshKey={activityLogRefreshKey}
      />

      <TlRecordingAudioModal
        open={audioPreview.open}
        url={audioPreview.url}
        title={audioPreview.title}
        onClose={() => setAudioPreview({ open: false, url: "", title: "", recordInfo: null })}
        isDark={isDark}
        labelClass={labelClass}
        apiBase={apiBase}
        recordInfo={audioPreview.recordInfo}
      />

      <CdrRecordingPicker
        apiBase={apiBase}
        open={pickerOpen}
        basketOnly
        onClose={() => { setPickerOpen(false); setPickerTarget(null); }}
        agentId={pickerTarget?.agentId || undefined}
        reportDate={pickerTarget?.reportDate || undefined}
        fieldLabel={RECORDING_URL_FIELDS.find((f) => f.key === pickerTarget?.fieldKey)?.label || ""}
        agents={agentOptions}
        isDark={isDark}
        onSelect={(url) => {
          if (!pickerTarget) return;
          if (pickerTarget.mode === "draft") {
            setDraftRows((prev) => prev.map((row) => (
              row._draftId === pickerTarget.draftId
                ? { ...row, [pickerTarget.fieldKey]: url }
                : row
            )));
          } else if (pickerTarget.mode === "inline") {
            setEditBuffer((f) => ({ ...f, [pickerTarget.fieldKey]: url }));
          }
        }}
      />
    </div>
  );
};

export default TeamLeaderRecordingReportsPage;
